import type { PaginationRequest, PopulateParam } from '@/interfaces/strapi';
import { buildPopulate } from './buildPopulate';

function buildFieldsParams(fields?: string[]): string[] {
	if (!fields?.length) return [];
	return fields.map((f, i) => `fields[${i}]=${encodeURIComponent(f)}`);
}

export function buildFilters(prefix: string, obj: Record<string, any>): string[] {
	return Object.entries(obj).flatMap(([key, val]) => {
		const keyPart = key.startsWith('$') ? key : encodeURIComponent(key);
		const p = `${prefix}[${keyPart}]`;

		if (val === null || val === undefined) return [];

		if (typeof val === 'object' && !Array.isArray(val)) {
			return buildFilters(p, val);
		}

		const v = Array.isArray(val) ? val.join(',') : String(val);
		return [`${p}=${encodeURIComponent(v)}`];
	});
}

type BuildStrapiUrlOptions = {
	base: string;
	pagination?: PaginationRequest;
	// Nota: soportamos tanto 'filter' como 'filters' por compatibilidad
	filter?: Record<string, any>;
	filters?: Record<string, any>;
	additionalFilters?: Record<string, any>;
	sort?: string;
	fields?: string[];
	populate?: PopulateParam;
	query?: Record<string, string | number | boolean>;
};

export const buildStrapiUrl = ({
	base,
	pagination,
	filter,
	filters,
	additionalFilters,
	sort,
	fields,
	populate,
	query,
}: BuildStrapiUrlOptions): string => {
	const params: string[] = [];

	if (pagination) {
		if (pagination.page !== undefined) params.push(`pagination[page]=${pagination.page}`);
		if (pagination.pageSize !== undefined) params.push(`pagination[pageSize]=${pagination.pageSize}`);
	}

	if (sort) {
		params.push(`sort=${encodeURIComponent(sort)}`);
	}

	if (fields?.length) {
		params.push(...buildFieldsParams(fields));
	}

	if (populate !== undefined) {
		params.push(...buildPopulate(populate));
	}

	const combined = { ...(filters ?? filter ?? {}), ...(additionalFilters ?? {}) };
	if (Object.keys(combined).length > 0) {
		params.push(...buildFilters('filters', combined));
	}

	if (query) {
		for (const [k, v] of Object.entries(query)) {
			params.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
		}
	}

	return params.length ? `${base}?${params.join('&')}` : base;
};
